import React, { useState, useEffect, useRef } from 'react';
import { Typography, FlexGrid, Notification, Button, Spinner } from '@telus-uds/components-web';
import styled from 'styled-components';
import DatePicker from 'react-datepicker';
import FileDownload from 'js-file-download';
import axios from 'axios';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  ResponsiveContainer
} from 'recharts';

import { reportsAPI, exportReportCsv } from '../utils/apiService';
import {
  formatNumber,
  formatPercentage,
  getBlackFridayDate
} from '../utils/formatters';
import SummaryCard from '../components/SummaryCard';
import { orderTypeName } from '../utils/orderTypeLabels';
import ChartDescription from '../components/ChartDescription';
import CategoryAxisTick from '../components/CategoryAxisTick';
import LastUpdated from '../components/LastUpdated';
import BaselineBadge from '../components/BaselineBadge';
import ShareBarChart, { toShareData } from '../components/ShareBarChart';
import LoadingSpinner from '../components/LoadingSpinner';
import EmptyState from '../components/EmptyState';
import { apiHost, config } from '../../utils/request';
import { chartCardStyle } from '../utils/chartStyles';
import 'react-datepicker/dist/react-datepicker.css';

const HeroSection = styled.div`
  background: linear-gradient(135deg, #4b286d 0%, #0066cc 100%);
  color: white;
  padding: 3rem 2rem;
  border-radius: 12px;
  margin-bottom: 2rem;
  text-align: center;
`;

const HeroTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
`;

const HeroSubtitle = styled.p`
  font-size: 1.25rem;
  opacity: 0.9;
`;

const ComparisonCard = styled.div`
  background: #ffffff;
  border: 2px solid #e4e4e4;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  text-align: center;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
    transform: translateY(-4px);
  }
`;

const MetricLabel = styled.div`
  font-size: 0.875rem;
  color: #666666;
  margin-bottom: 0.5rem;
  font-weight: 500;
`;

const CurrentValue = styled.div`
  font-size: 3rem;
  font-weight: 700;
  color: #4b286d;
  margin-bottom: 0.5rem;
`;

const PreviousValue = styled.div`
  font-size: 1rem;
  color: #999999;
  margin-bottom: 1rem;
`;

const PercentageChange = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: ${props => props.color || '#00A600'};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
`;

const WindowInputsRow = styled.div`
  display: flex;
  gap: 1.5rem;
  justify-content: center;
  margin-top: 1rem;

  label {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    font-size: 0.875rem;
    font-weight: 500;
    color: #454545;
  }

  input {
    width: 70px;
    padding: 0.4rem;
    border: 1px solid #ccc;
    border-radius: 4px;
    text-align: center;
  }
`;

const DatePickerWrapper = styled.div`
  margin-bottom: 2rem;
  text-align: center;

  .react-datepicker-wrapper {
    display: inline-block;
  }

  input {
    padding: 0.75rem 1.5rem;
    border: 2px solid #4b286d;
    border-radius: 8px;
    font-size: 1rem;
    text-align: center;
    cursor: pointer;

    &:focus {
      outline: none;
      border-color: #0066cc;
    }
  }
`;

export const REPORT_PDF_POLL_INTERVAL_MS = 2000;
export const REPORT_PDF_MAX_POLL_ATTEMPTS = 60; // ~2 minutes bounded polling — never an infinite interval

// Bounded status poll. `isCancelled()` is checked before every scheduled tick so an unmount
// (or a fresh export request) stops the loop cleanly with no stray timers left behind.
// Exported for direct unit testing (index.test.js) without rendering the full chart-heavy component.
export const pollUntilSettled = async (jobId, isCancelled) => {
  for (let attempt = 0; attempt < REPORT_PDF_MAX_POLL_ATTEMPTS; attempt += 1) {
    if (isCancelled()) return 'CANCELLED';
    // eslint-disable-next-line no-await-in-loop
    const { data } = await reportsAPI.getBlackFridayPdfStatus(jobId);
    if (data.status === 'AVAILABLE' || data.status === 'FAILED') return data.status;
    // eslint-disable-next-line no-await-in-loop, no-loop-func
    await new Promise(resolve => { setTimeout(resolve, REPORT_PDF_POLL_INTERVAL_MS); });
  }
  return 'TIMEOUT';
};

// Format in local timezone to avoid UTC conversion issues (new Date('YYYY-MM-DD') parses as
// UTC, which can shift a day earlier/later for Canadian timezones).
const formatLocalDate = date => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const BlackFridaySpecialReport = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [selectedDate, setSelectedDate] = useState(getBlackFridayDate(new Date().getFullYear()));
  const [daysBefore, setDaysBefore] = useState(7);
  const [daysAfter, setDaysAfter] = useState(7);
  const [isExporting, setIsExporting] = useState(false);
  const [pdfExportStatus, setPdfExportStatus] = useState(null);
  const pollCancelledRef = useRef(false);
  // Snapshot of whichever date/window produced the data currently on screen — export must use
  // this, not the live date picker state, or changing the date without clicking "Load Report"
  // would export a file that doesn't match what's displayed.
  const [appliedParams, setAppliedParams] = useState(null);

  useEffect(() => {
    return () => {
      // Stop polling on unmount — the in-flight loop checks this flag before every tick.
      pollCancelledRef.current = true;
    };
  }, []);

  // refresh=true asks the server to skip its cached result (Refresh button).
  const fetchData = async (refresh = false) => {
    setLoading(true);
    setError(null);
    try {
      const dateString = formatLocalDate(selectedDate);

      setAppliedParams({ dateString, daysBefore, daysAfter });
      const result = await reportsAPI.getBlackFridayReport(dateString, refresh === true ? { daysBefore, daysAfter, refresh: true } : { daysBefore, daysAfter });
      setData(result.data);
      setMeta({ generatedAt: result.generated_at, throttled: Boolean(result.refresh_throttled) });
    } catch (err) {
      setError(err.message || 'Failed to fetch Black Friday data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleDateChange = date => {
    setSelectedDate(date);
  };

  const handleApply = () => {
    fetchData();
  };

  const handleExportExcel = async () => {
    try {
      setIsExporting(true);
      // Use the date the currently-displayed report was actually loaded with, not whatever
      // is live in the date picker (which may have been changed without clicking Load Report).
      const dateString = (appliedParams && appliedParams.dateString) || formatLocalDate(selectedDate);

      const result = await axios.get(
        `${apiHost}/reports/black-friday/export/excel?date=${dateString}`,
        {
          ...config(),
          responseType: 'arraybuffer'
        }
      );
      
      if (result) {
        FileDownload(result.data, `black-friday-report-${dateString}.xlsx`);
      }
    } catch (err) {
      alert('Failed to export Excel file. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  // CSV of the report that is on screen: uses the date it was loaded with, not the live picker.
  const handleExportCsv = async () => {
    try {
      setIsExporting(true);
      const dateString = (appliedParams && appliedParams.dateString) || formatLocalDate(selectedDate);
      await exportReportCsv('/reports/black-friday/export/csv', { date: dateString }, 'black-friday');
    } catch (err) {
      alert('Failed to export CSV file. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportPdf = async () => {
    // Use the date the currently-displayed report was actually loaded with, not whatever is
    // live in the date picker (which may have been changed without clicking Load Report).
    const dateString = (appliedParams && appliedParams.dateString) || formatLocalDate(selectedDate);

    setIsExporting(true);
    setPdfExportStatus('PREPARING');
    pollCancelledRef.current = false;

    try {
      const enqueueResult = await reportsAPI.requestBlackFridayPdf(dateString);
      const { jobId } = enqueueResult.data;

      const status = await pollUntilSettled(jobId, () => pollCancelledRef.current);
      if (pollCancelledRef.current) return;

      if (status === 'FAILED') {
        setPdfExportStatus('FAILED');
        alert('Report generation failed. Please try again.');
        return;
      }
      if (status !== 'AVAILABLE') {
        // Bounded-polling timeout — not a hard failure, but we stop waiting.
        setPdfExportStatus('FAILED');
        alert('Report is taking longer than expected. Please try again shortly.');
        return;
      }

      setPdfExportStatus('AVAILABLE');
      // `?redirect=false` returns the presigned S3 URL as JSON instead of a 302 the browser would
      // follow itself — following the 302 via axios/XHR makes the browser issue a CORS-preflighted
      // cross-origin request straight to S3, which has no CORS policy for this origin and gets
      // blocked. window.open() is a plain navigation and is never CORS-checked.
      const result = await axios.get(
        `${apiHost}/reports/black-friday/export/pdf/${jobId}/download?redirect=false`,
        config()
      );
      const presignedUrl = result && result.data && result.data.data && result.data.data.url;
      if (presignedUrl) {
        window.open(presignedUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      if (!pollCancelledRef.current) {
        setPdfExportStatus('FAILED');
        alert('Failed to export PDF file. Please try again.');
      }
    } finally {
      if (!pollCancelledRef.current) {
        setIsExporting(false);
        setPdfExportStatus(null);
      }
    }
  };

  // Rendered on every branch below (loading/error/empty/happy path) so a failed or empty load
  // never strands the user without a way to change the date and retry.
  const datePickerControls = (
    <DatePickerWrapper>
      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
        Select Black Friday Date:
      </label>
      <DatePicker
        selected={selectedDate}
        onChange={handleDateChange}
        dateFormat="yyyy-MM-dd"
        placeholderText="Select date"
      />
      <WindowInputsRow>
        <label>
          Days Before
          <input
            type="number"
            min={1}
            max={30}
            value={daysBefore}
            onChange={e => setDaysBefore(Number(e.target.value) || 1)}
          />
        </label>
        <label>
          Days After
          <input
            type="number"
            min={1}
            max={30}
            value={daysAfter}
            onChange={e => setDaysAfter(Number(e.target.value) || 1)}
          />
        </label>
      </WindowInputsRow>
      <div style={{ marginTop: '1rem' }}>
        <Button variant={{ priority: 'high' }} onClick={handleApply}>
          Load Report
        </Button>
      </div>
    </DatePickerWrapper>
  );

  if (loading) {
    return (
      <div>
        {datePickerControls}
        <LoadingSpinner text="Loading Black Friday Report..." />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div>
        {datePickerControls}
        <Notification variant={{ validation: 'error' }}>
          <div>
            <strong>Error:</strong> {error}
          </div>
        </Notification>
      </div>
    );
  }

  if (!data) {
    return (
      <div>
        {datePickerControls}
        <EmptyState title="No Data Available" />
      </div>
    );
  }

  const { black_friday_stats, comparison, daily_trend, window_by_type } = data;
  const orders = black_friday_stats && black_friday_stats.orders;
  const pdfs = black_friday_stats && black_friday_stats.pdfs;
  const emails = black_friday_stats && black_friday_stats.emails;
  const previous_week_daily_average = comparison && comparison.previous_week_daily_average;
  const percentage_increase = comparison && comparison.percentage_increase;

  // Future-date guard: the selected date hasn't happened yet, so 0%/▼0.00% cards would be
  // misleading — show an explicit "hasn't happened" state instead (QA P2 finding).
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const selectedDayStart = new Date(selectedDate);
  selectedDayStart.setHours(0, 0, 0, 0);
  if (selectedDayStart > todayStart) {
    return (
      <div>
        {datePickerControls}
        <EmptyState
          icon="🗓️"
          title="Event Hasn't Happened Yet"
          description={`${selectedDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })} is in the future — there's no data to report yet.`}
        />
      </div>
    );
  }

  // Zero-data guard: a real (past/today) date with no activity at all is a "nothing happened"
  // state, not a meaningful 0% result — avoid showing misleading comparison percentages.
  const totalOrders = (orders && orders.total_orders) || 0;
  const totalPdfs = (pdfs && pdfs.summary && pdfs.summary.total_pdfs_generated) || 0;
  const totalEmails = (emails && emails.summary && emails.summary.total_emails_sent) || 0;
  if (totalOrders === 0 && totalPdfs === 0 && totalEmails === 0) {
    return (
      <div>
        {datePickerControls}
        <EmptyState
          icon="📭"
          title="No Data for This Period"
          description="There were no orders, PDFs, or emails recorded on this date."
        />
      </div>
    );
  }

  // Totals across the whole ±daysBefore/daysAfter window (drives the window summary cards
  // below the trend chart — the single-day cards above stay scoped to the BF day itself).
  const windowTotals = (daily_trend || []).reduce((acc, item) => ({
    orders: acc.orders + (item.orders || 0),
    pdfs: acc.pdfs + (item.pdfs || 0),
    emails: acc.emails + (item.emails || 0)
  }), { orders: 0, pdfs: 0, emails: 0 });
  const windowDays = (daily_trend || []).length;
  const windowDailyAvg = {
    orders: windowDays > 0 ? Math.round(windowTotals.orders / windowDays) : 0,
    pdfs: windowDays > 0 ? Math.round(windowTotals.pdfs / windowDays) : 0,
    emails: windowDays > 0 ? Math.round(windowTotals.emails / windowDays) : 0
  };

  // Use the selected date (from date picker) for display consistency
  const formattedDate = selectedDate.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // Prepare chart data for orders by type
  const ordersTypeData = orders && orders.by_type && orders.by_type.map(item => ({
    name: orderTypeName(item.type_of_order) || 'Unknown',
    rawValue: item.type_of_order || 'Unknown',
    submitted: parseInt(item.submitted, 10) || 0,
    value: parseInt(item.count, 10)
  })) || [];

  // Window-wide by-type breakdowns (separate for orders/pdfs/emails), used by the Window
  // Totals section below the trend chart.
  const windowOrdersTypeData = (window_by_type && window_by_type.orders || []).map(item => ({
    name: orderTypeName(item.type || item.label) || 'Unknown',
    count: item.count
  }));
  const windowPdfsTypeData = (window_by_type && window_by_type.pdfs || []).map(item => ({
    name: item.label || 'Unknown',
    count: item.count
  }));
  const windowEmailsTypeData = (window_by_type && window_by_type.emails || []).map(item => ({
    name: (item.label || 'Unknown').replace(/_/g, ' '),
    count: item.count
  }));

  // Prepare comparison data for all metrics
  const comparisonData = [
    {
      metric: 'Orders',
      blackFriday: (orders && orders.total_orders) || 0,
      previousAvg: (previous_week_daily_average && previous_week_daily_average.orders) || 0,
      increase: (percentage_increase && percentage_increase.orders) || 0
    },
    {
      metric: 'PDFs',
      blackFriday: (pdfs && pdfs.summary && pdfs.summary.total_pdfs_generated) || 0,
      previousAvg: (previous_week_daily_average && previous_week_daily_average.pdfs) || 0,
      increase: (percentage_increase && percentage_increase.pdfs) || 0
    },
    {
      metric: 'Emails',
      blackFriday: (emails && emails.summary && emails.summary.total_emails_sent) || 0,
      previousAvg: (previous_week_daily_average && previous_week_daily_average.emails) || 0,
      increase: (percentage_increase && percentage_increase.emails) || 0
    }
  ];

  return (
    <div>
      <HeroSection>
        <HeroTitle>🎉 Black Friday Report {selectedDate.getFullYear()}</HeroTitle>
        <HeroSubtitle>{formattedDate}</HeroSubtitle>
      </HeroSection>

      {datePickerControls}

      <LastUpdated
        generatedAt={meta && meta.generatedAt}
        throttled={Boolean(meta && meta.throttled)}
        loading={loading}
        onRefresh={() => fetchData(true)}
      />

      {/* Main Comparison Cards */}
      <FlexGrid>
        <FlexGrid.Row>
          {comparisonData.map(item => (
            <FlexGrid.Col key={item.metric} xs={12} md={4}>
              <ComparisonCard>
                <MetricLabel>{item.metric}</MetricLabel>
                <CurrentValue>{formatNumber(item.blackFriday)}</CurrentValue>
                <PreviousValue>
                  Previous Week Avg: {formatNumber(item.previousAvg)}
                </PreviousValue>
                <PercentageChange color={item.increase > 0 ? '#00A600' : '#CC0000'}>
                  {item.increase > 0 ? '▲' : '▼'} {formatPercentage(item.increase, 2)}
                </PercentageChange>
              </ComparisonCard>
            </FlexGrid.Col>
          ))}
        </FlexGrid.Row>
      </FlexGrid>

      {/* Summary Statistics */}
      <div style={{ marginTop: '3rem', marginBottom: '2rem' }}>
        <Typography heading="h2" variant={{ size: 'h2' }} block style={{ color: '#4b286d', marginBottom: '1.5rem' }}>
          Black Friday Statistics
        </Typography>
        <FlexGrid>
          <FlexGrid.Row>
            <FlexGrid.Col xs={12} md={4} style={{ padding: '0 0.75rem', marginBottom: '1.5rem' }}>
              <SummaryCard
                title="Orders Created" descriptionKey="bfOrdersCreated"
                extra={<BaselineBadge metric={data.metrics && data.metrics.orders_created} baseline={data.baseline} />}
                value={formatNumber(orders && orders.total_orders)}
                icon="📦"
                color="#0066CC"
              />
            </FlexGrid.Col>
            <FlexGrid.Col xs={12} md={4} style={{ padding: '0 0.75rem', marginBottom: '1.5rem' }}>
              <SummaryCard
                title="Orders Submitted" descriptionKey="bfOrdersSubmitted"
                extra={<BaselineBadge metric={data.metrics && data.metrics.orders_submitted} baseline={data.baseline} />}
                value={formatNumber(orders && orders.submitted)}
                icon="📨"
                color="#0066CC"
              />
            </FlexGrid.Col>
            <FlexGrid.Col xs={12} md={4} style={{ padding: '0 0.75rem', marginBottom: '1.5rem' }}>
              <SummaryCard
                title="Total PDFs" descriptionKey="bfPdfs"
                extra={<BaselineBadge metric={data.metrics && data.metrics.pdfs_generated} baseline={data.baseline} />}
                value={formatNumber(pdfs && pdfs.summary && pdfs.summary.total_pdfs_generated)}
                icon="📄"
                color="#4b286d"
              />
            </FlexGrid.Col>
            <FlexGrid.Col xs={12} md={4} style={{ padding: '0 0.75rem', marginBottom: '1.5rem' }}>
              <SummaryCard
                title="Total Emails" descriptionKey="bfEmails"
                extra={<BaselineBadge metric={data.metrics && data.metrics.emails_sent} baseline={data.baseline} />}
                value={formatNumber(emails && emails.summary && emails.summary.total_emails_sent)}
                icon="📧"
                color="#00A600"
              />
            </FlexGrid.Col>
          </FlexGrid.Row>
        </FlexGrid>
      </div>

      {/* Charts Section */}
      <FlexGrid style={{ marginTop: '3rem' }}>
        <FlexGrid.Row>
          <FlexGrid.Col xs={12} md={6} style={{ padding: '0 0.75rem', marginBottom: '2rem' }}>
            <div
              style={chartCardStyle}
            >
              <Typography heading="h3" variant={{ size: 'h3' }} block style={{ marginBottom: '1rem', color: '#4b286d' }}>
                Orders by Type
              </Typography>
              <ChartDescription descriptionKey="ordersByType" />
              <ShareBarChart data={toShareData(ordersTypeData)} title="Orders by Type" showSubmitted />
            </div>
          </FlexGrid.Col>

          <FlexGrid.Col xs={12} md={6} style={{ padding: '0 0.75rem', marginBottom: '2rem' }}>
            <div
              style={chartCardStyle}
            >
              <Typography heading="h3" variant={{ size: 'h3' }} block style={{ marginBottom: '1rem', color: '#4b286d' }}>
                Comparison with Previous Week
              </Typography>
              <ChartDescription descriptionKey="comparisonWithPreviousWeek" />
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={comparisonData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="metric" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="blackFriday" fill="#4b286d" name="Black Friday" />
                  <Bar dataKey="previousAvg" fill="#cccccc" name="Previous Week Avg" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </FlexGrid.Col>
        </FlexGrid.Row>
      </FlexGrid>

      {/* Daily Trend (Black Friday ± N days) */}
      {Array.isArray(daily_trend) && daily_trend.length > 0 && (
        <div style={{ marginTop: '3rem', marginBottom: '1.5rem' }}>
          <Typography heading="h2" variant={{ size: 'h2' }} block style={{ color: '#4b286d', marginBottom: '1.5rem' }}>
            Window Totals (±{daysBefore}/{daysAfter} days)
          </Typography>
          <FlexGrid>
            <FlexGrid.Row>
              <FlexGrid.Col xs={12} md={4} style={{ padding: '0 0.75rem', marginBottom: '1.5rem' }}>
                <SummaryCard
                  title="Orders in Window" descriptionKey="windowOrders"
                  value={formatNumber(windowTotals.orders)}
                  subText={`Avg ${formatNumber(windowDailyAvg.orders)}/day over ${windowDays} days`}
                  icon="📦"
                  color="#0066CC"
                />
              </FlexGrid.Col>
              <FlexGrid.Col xs={12} md={4} style={{ padding: '0 0.75rem', marginBottom: '1.5rem' }}>
                <SummaryCard
                  title="PDFs in Window" descriptionKey="windowPdfs"
                  value={formatNumber(windowTotals.pdfs)}
                  subText={`Avg ${formatNumber(windowDailyAvg.pdfs)}/day over ${windowDays} days`}
                  icon="📄"
                  color="#4b286d"
                />
              </FlexGrid.Col>
              <FlexGrid.Col xs={12} md={4} style={{ padding: '0 0.75rem', marginBottom: '1.5rem' }}>
                <SummaryCard
                  title="Emails in Window" descriptionKey="windowEmails"
                  value={formatNumber(windowTotals.emails)}
                  subText={`Avg ${formatNumber(windowDailyAvg.emails)}/day over ${windowDays} days`}
                  icon="📧"
                  color="#00A600"
                />
              </FlexGrid.Col>
            </FlexGrid.Row>
          </FlexGrid>
        </div>
      )}

      {/* Window by-type breakdown — separate chart per metric (orders/pdfs/emails) */}
      {window_by_type && (
        <FlexGrid style={{ marginBottom: '2rem' }}>
          <FlexGrid.Row>
            <FlexGrid.Col xs={12} md={4} style={{ padding: '0 0.75rem', marginBottom: '2rem' }}>
              <div
                style={chartCardStyle}
              >
                <Typography heading="h3" variant={{ size: 'h3' }} block style={{ marginBottom: '1rem', color: '#4b286d' }}>
                  Orders by Type (Window)
                </Typography>
              <ChartDescription descriptionKey="windowOrdersByType" />
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={windowOrdersTypeData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" height={90} interval={0} tickLine={false} tick={<CategoryAxisTick />} />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill="#0066CC" name="Orders" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </FlexGrid.Col>

            <FlexGrid.Col xs={12} md={4} style={{ padding: '0 0.75rem', marginBottom: '2rem' }}>
              <div
                style={chartCardStyle}
              >
                <Typography heading="h3" variant={{ size: 'h3' }} block style={{ marginBottom: '1rem', color: '#4b286d' }}>
                  PDFs by Type (Window)
                </Typography>
              <ChartDescription descriptionKey="windowPdfsByType" />
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={windowPdfsTypeData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" height={90} interval={0} tickLine={false} tick={<CategoryAxisTick />} />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill="#4b286d" name="PDFs" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </FlexGrid.Col>

            <FlexGrid.Col xs={12} md={4} style={{ padding: '0 0.75rem', marginBottom: '2rem' }}>
              <div
                style={chartCardStyle}
              >
                <Typography heading="h3" variant={{ size: 'h3' }} block style={{ marginBottom: '1rem', color: '#4b286d' }}>
                  Emails by Type (Window)
                </Typography>
              <ChartDescription descriptionKey="windowEmailsByType" />
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={windowEmailsTypeData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" height={90} interval={0} tickLine={false} tick={<CategoryAxisTick />} />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill="#00A600" name="Emails" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </FlexGrid.Col>
          </FlexGrid.Row>
        </FlexGrid>
      )}

      {Array.isArray(daily_trend) && daily_trend.length > 0 && (
        <div
          style={{
            marginTop: '2rem',
            marginBottom: '2rem',
            background: '#fff',
            padding: '1.5rem',
            borderRadius: '8px',
            border: '1px solid #e4e4e4'
          }}
        >
          <Typography heading="h3" variant={{ size: 'h3' }} block style={{ marginBottom: '1rem', color: '#4b286d' }}>
            Daily Trend (±{daysBefore}/{daysAfter} days)
          </Typography>
              <ChartDescription descriptionKey="dailyTrend" />
          <ResponsiveContainer width="100%" height={320}>
            <LineChart data={daily_trend}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tickFormatter={d => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              />
              <YAxis />
              <Tooltip labelFormatter={d => new Date(d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} />
              <Legend />
              <ReferenceLine
                x={`${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`}
                stroke="#CC0000"
                strokeDasharray="4 4"
                label={{ value: 'Black Friday', position: 'top', fill: '#CC0000', fontSize: 12 }}
              />
              <Line type="monotone" dataKey="orders" stroke="#0066CC" strokeWidth={2} />
              <Line type="monotone" dataKey="pdfs" stroke="#4b286d" strokeWidth={2} />
              <Line type="monotone" dataKey="emails" stroke="#00A600" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Export Section */}
      <div style={{ marginTop: '3rem', marginBottom: '2rem', textAlign: 'center', display: 'flex', justifyContent: 'center', gap: '1.5rem' }}>
        <Spinner
          label={pdfExportStatus === 'PREPARING' ? 'Preparing report…' : 'Exporting...'}
          variant={{ size: 'small' }}
          show={isExporting}
          inline
        >
          <div>
            <Button variant={{ priority: 'high' }} onClick={handleExportPdf} disabled={isExporting}>
              📥 Download Report as PDF
            </Button>
          </div>
        </Spinner>
        <Spinner label="Exporting..." variant={{ size: 'small' }} show={isExporting} inline>
          <div>
            <Button variant={{ priority: 'low' }} onClick={handleExportExcel}>
              📊 Export to Excel
            </Button>
          </div>
        </Spinner>
        <Spinner label="Exporting..." variant={{ size: 'small' }} show={isExporting} inline>
          <div>
            <Button variant={{ priority: 'low' }} onClick={handleExportCsv}>
              📄 Export to CSV
            </Button>
          </div>
        </Spinner>
      </div>
    </div>
  );
};

export default BlackFridaySpecialReport;
